import React, { useRef, useEffect, useState } from 'react';
import Quagga from 'quagga';
import { getNutritionByUPC, validateUPC } from '../utils/nutritionixAPI';
import './BarcodeScanner.css';

function BarcodeScanner({ onFoodFound, onClose }) {
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [foundProduct, setFoundProduct] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);
  const processingRef = useRef(false);

  const addDebugLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev.slice(-10), { timestamp, message }]);
  };

  useEffect(() => {
    if (!scanning) return;

    console.log('🎬 Initializing QuaggaJS...');
    addDebugLog('📷 Starting camera...');

    Quagga.init({
      inputStream: {
        name: "Live",
        type: "LiveStream",
        target: scannerRef.current,
        constraints: {
          facingMode: "environment",
          width: { min: 640, ideal: 1280 },
          height: { min: 480, ideal: 720 }
        }
      },
      decoder: {
        readers: ["ean_reader", "upc_reader", "code_128_reader"]
      },
      locate: false, // Disable locator for better performance
      numOfWorkers: 0, // Run in main thread on mobile
      frequency: 5 // Scan 5 times per second
    }, (err) => {
      if (err) {
        console.error('❌ QuaggaJS error:', err);
        addDebugLog(`❌ Error: ${err.message || err}`);
        setError('Unable to access camera');
        return;
      }

      console.log('✅ QuaggaJS initialized');
      addDebugLog('✅ Camera ready');
      Quagga.start();
      console.log('🎥 Scanning started');
      addDebugLog('🎥 Scanning...');
    });

    // Handle detection
    const onDetected = (result) => {
      if (processingRef.current || !result || !result.codeResult) return;

      const code = result.codeResult.code;

      // Check confidence - only accept high-quality reads
      // QuaggaJS returns errors array where lower values = better quality
      const errors = result.codeResult.decodedCodes
        ?.map(d => d.error)
        .filter(e => e !== undefined);

      const avgError = errors?.length
        ? errors.reduce((sum, err) => sum + err, 0) / errors.length
        : 1;

      console.log('📷 DETECTED:', code, 'Error rate:', avgError.toFixed(3));

      // Reject low-quality scans (error rate > 0.08 means uncertain read)
      if (avgError > 0.08) {
        console.log('⚠️ Low confidence (error rate too high), skipping');
        return;
      }

      // Also check that we have reasonable confidence data
      if (!errors || errors.length === 0) {
        console.log('⚠️ No confidence data, skipping');
        return;
      }

      // Skip duplicates
      if (code === lastScannedCode) {
        console.log('⏭️ Duplicate, skipping');
        return;
      }

      processingRef.current = true;
      setLastScannedCode(code);
      setScanning(false);
      Quagga.stop();

      console.log('✅ New barcode:', code);
      addDebugLog(`📷 Detected: ${code}`);

      // Show loading
      setLoading(true);
      setError(null);
      setSuccess(false);

      console.log('🔍 Validating...');
      addDebugLog('🔍 Validating...');

      const isValid = validateUPC(code);
      console.log('Validation:', isValid ? '✅' : '⚠️ Check digit warning');

      // Don't block on validation - let API be the final authority
      // Some barcodes have unusual formats that might not validate
      addDebugLog(isValid ? '✅ Looking up...' : '⚠️ Trying API anyway...');
      console.log('🌐 API lookup:', code);

      getNutritionByUPC(code)
        .then(foodData => {
          console.log('✅ Found:', foodData.food_name);
          addDebugLog(`✅ Found: ${foodData.food_name}`);

          setLoading(false);
          setSuccess(true);
          setFoundProduct(foodData);

          setTimeout(() => {
            onFoodFound(foodData);
          }, 1500);
        })
        .catch(err => {
          console.error('❌ API error:', err);
          addDebugLog(`❌ ${err.message}`);

          const msg = err.message.includes('not found') || err.message.includes('resource not found')
            ? `Product not in database (${code})`
            : err.message || 'Product not found';

          setError(msg);
          setLoading(false);
          processingRef.current = false;
          setTimeout(() => {
            setLastScannedCode(null);
            setScanning(true);
          }, 4000);
        });
    };

    Quagga.onDetected(onDetected);

    return () => {
      console.log('🧹 Cleanup');
      Quagga.offDetected(onDetected);
      Quagga.stop();
    };
  }, [scanning, onFoodFound, lastScannedCode]);

  const handleManualEntry = () => {
    const barcode = prompt('Enter barcode manually:', lastScannedCode || '');
    if (!barcode) return;

    setScanning(false);
    setLoading(true);
    setError(null);

    getNutritionByUPC(barcode)
      .then(foodData => {
        onFoodFound(foodData);
      })
      .catch(err => {
        const msg = err.message.includes('not found')
          ? `UPC ${barcode} not in database. Try Search Foods or AI Estimator.`
          : err.message || 'Product not found';
        setError(msg);
        setLoading(false);
        setTimeout(() => setScanning(true), 5000);
      });
  };

  return (
    <div className="barcode-scanner-modal">
      <div className="scanner-container">
        <div className="scanner-header">
          <h3>📷 Scan Barcode</h3>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="scanner-content">
          {scanning && (
            <>
              <div ref={scannerRef} style={{ width: '100%', minHeight: '300px' }}></div>
              <p style={{ marginTop: '15px', color: '#7f8c8d', textAlign: 'center' }}>
                Position barcode in center of camera view
              </p>
            </>
          )}

          {loading && (
            <div style={{
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px',
              padding: '40px 20px'
            }}>
              <div className="spinner"></div>
              <p style={{ color: '#2c3e50', fontSize: '20px', fontWeight: 700, margin: 0 }}>
                🔍 Looking up product...
              </p>
              {lastScannedCode && (
                <p style={{ color: '#7f8c8d', fontSize: '16px', margin: 0 }}>
                  UPC: {lastScannedCode}
                </p>
              )}
            </div>
          )}

          {success && foundProduct && (
            <div style={{
              minHeight: '300px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px',
              backgroundColor: '#d4edda',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '64px' }}>✅</div>
              <h3 style={{ color: '#155724', margin: 0, fontSize: '24px' }}>
                Product Found!
              </h3>
              <p style={{ color: '#155724', fontSize: '18px', fontWeight: 600, margin: 0 }}>
                {foundProduct.brand_name && `${foundProduct.brand_name} - `}
                {foundProduct.food_name}
              </p>
              <p style={{ color: '#155724', fontSize: '16px', margin: 0 }}>
                {Math.round(foundProduct.nf_calories)} calories
              </p>
            </div>
          )}

          {error && (
            <div className="scanner-error">
              <p style={{ fontSize: '18px', marginBottom: '10px' }}>⚠️ {error}</p>
              {error.includes('not in database') && (
                <div style={{
                  backgroundColor: 'rgba(52, 152, 219, 0.1)',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>Try these instead:</p>
                  <p style={{ margin: '0' }}>
                    • Use <strong>Search Foods</strong> to find similar items<br/>
                    • Use <strong>AI Estimator</strong> to describe the food
                  </p>
                </div>
              )}
              <button
                onClick={() => {
                  setError(null);
                  setLastScannedCode(null);
                  processingRef.current = false;
                  setScanning(true);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 600
                }}
              >
                Scan Another
              </button>
            </div>
          )}
        </div>

        {debugLogs.length > 0 && (
          <div style={{
            backgroundColor: '#0a0a0a',
            border: '2px solid #4ECDC4',
            borderRadius: '8px',
            padding: '10px',
            margin: '15px 20px',
            maxHeight: '150px',
            overflowY: 'auto',
            fontFamily: 'monospace',
            fontSize: '11px'
          }}>
            <div style={{ color: '#4ECDC4', fontWeight: 'bold', marginBottom: '5px' }}>
              📋 Debug Log:
            </div>
            {debugLogs.map((log, index) => (
              <div key={index} style={{ color: '#aaa', marginBottom: '3px' }}>
                <span style={{ color: '#666' }}>[{log.timestamp}]</span> {log.message}
              </div>
            ))}
          </div>
        )}

        <div className="scanner-actions">
          <button className="manual-entry-button" onClick={handleManualEntry}>
            ⌨️ Enter Manually
          </button>
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
        </div>

        <div className="scanner-tips">
          <p><strong>Tips:</strong></p>
          <ul>
            <li>Good lighting is essential</li>
            <li>Hold camera 6-8 inches from barcode</li>
            <li>Keep barcode flat and centered</li>
            <li>Try manual entry if scanning fails</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default BarcodeScanner;
